import { useState, useMemo, useEffect } from "react";
import { X, Check, Image as ImageIcon, AlertCircle } from "lucide-react";
import MediaFrame from "@/components/common/MediaFrame";
import { t } from "@/i18n";

/**
 * Extrae y normaliza las vistas guardadas de un personaje:
 * - character.views (lista explícita con { id, assetId, viewType, isMaster, label })
 * - character.masterAssetId (si no está ya en views)
 * - character.viewAssetIds (si no están ya en views)
 */
export function getCharacterSavedViews(character, assetById = {}) {
  if (!character) return [];
  const views = [];
  const seenAssetIds = new Set();

  if (Array.isArray(character.views)) {
    for (const v of character.views) {
      if (v && v.assetId && !seenAssetIds.has(v.assetId)) {
        seenAssetIds.add(v.assetId);
        views.push({
          id: v.id || `vw_${v.assetId}`,
          assetId: v.assetId,
          label: v.label || (v.isMaster ? (t.characters?.master || "Principal") : (t.characters?.view || "Vista")),
          viewType: v.viewType || (v.isMaster ? "front" : "other"),
          isMaster: Boolean(v.isMaster),
        });
      }
    }
  }

  if (character.masterAssetId && !seenAssetIds.has(character.masterAssetId)) {
    seenAssetIds.add(character.masterAssetId);
    views.unshift({
      id: `vw_master_${character.masterAssetId}`,
      assetId: character.masterAssetId,
      label: t.characters?.master || "Principal",
      viewType: "front",
      isMaster: true,
    });
  }

  if (Array.isArray(character.viewAssetIds)) {
    for (const aId of character.viewAssetIds) {
      if (aId && !seenAssetIds.has(aId)) {
        seenAssetIds.add(aId);
        views.push({
          id: `vw_${aId}`,
          assetId: aId,
          label: t.characters?.view || "Vista",
          viewType: "other",
          isMaster: false,
        });
      }
    }
  }

  return views;
}

/**
 * CharacterViewSelectorModal
 * Modal para seleccionar vistas guardadas de un personaje como referencias reales de generación.
 */
export const CharacterViewSelectorModal = ({
  open,
  onClose,
  character,
  assetById = {},
  alreadySelectedAssetIds = [],
  onConfirm,
  maxAllowed = 2,
  autoSelectMaster = true,
}) => {
  const views = useMemo(() => getCharacterSavedViews(character, assetById), [character, assetById]);

  // Selección local de vistas dentro del modal
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);

  useEffect(() => {
    if (open && character) {
      const initial = [];
      for (const v of views) {
        if (alreadySelectedAssetIds.includes(v.assetId)) {
          initial.push(v.assetId);
        }
      }
      if (autoSelectMaster && initial.length === 0 && views.length > 0) {
        const master = views.find((v) => v.isMaster) || views[0];
        if (master) initial.push(master.assetId);
      }
      setSelectedAssetIds(initial);
    }
  }, [open, character, views, alreadySelectedAssetIds, autoSelectMaster]);

  if (!open || !character) return null;

  const toggleViewSelection = (assetId) => {
    setSelectedAssetIds((prev) => {
      if (prev.includes(assetId)) {
        return prev.filter((id) => id !== assetId);
      }
      return [...prev, assetId];
    });
  };

  const handleConfirm = () => {
    const chosenViews = views
      .filter((v) => selectedAssetIds.includes(v.assetId))
      .map((v) => ({
        assetId: v.assetId,
        viewId: v.id,
        viewLabel: v.label,
        viewType: v.viewType,
        isMaster: v.isMaster,
        characterId: character.id,
        characterName: character.name,
        characterHandle: character.handle,
      }));

    onConfirm?.(chosenViews);
    onClose?.();
  };

  const hasViews = views.length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="character-view-selector-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm animate-fade-in"
    >
      <div className="relative flex flex-col w-full max-w-lg rounded-xl border border-[#2e2e2e] bg-[#141414] shadow-2xl overflow-hidden max-h-[85vh]">
        {/* Cabecera */}
        <div className="flex items-center justify-between border-b border-[#242424] px-5 py-4 bg-[#181818]">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs px-2 py-0.5 rounded bg-[#f5b544]/15 border border-[#f5b544]/30 text-[#f5b544] font-medium">
                @{character.handle}
              </span>
              <h2 className="text-sm font-semibold text-[#f4f1ec]">
                {character.name || `@${character.handle}`}
              </h2>
            </div>
            <p className="text-[11px] text-[#8e8e8e]">
              {t.characters?.selectViewsPrompt || "Elige las vistas guardadas que deseas usar como referencia visual para la generación."}
            </p>
          </div>
          <button
            type="button"
            data-testid="character-view-modal-close"
            onClick={onClose}
            aria-label={t.common?.close || "Cerrar"}
            className="rounded-lg p-1.5 text-[#888] hover:bg-[#252525] hover:text-[#f4f1ec] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto p-5">
          {!hasViews ? (
            <div
              data-testid="character-no-views-notice"
              className="flex flex-col items-center justify-center text-center py-8 px-4 rounded-lg border border-dashed border-[#2a2a2a] bg-[#111]"
            >
              <div className="h-10 w-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 mb-3">
                <AlertCircle size={20} />
              </div>
              <h3 className="text-sm font-medium text-[#ededed] mb-1">
                {t.characters?.noViewsTitle || "Sin vistas guardadas"}
              </h3>
              <p className="text-xs text-[#888] max-w-sm mb-4 leading-relaxed">
                {t.characters?.noViewsDesc ||
                  "Este personaje no tiene vistas guardadas en la biblioteca. Puedes añadir vistas desde la ficha del personaje o usar referencias manuales."}
              </p>
              <p className="text-[11px] text-[#666] italic">
                {t.characters?.noViewAttachedNote ||
                  "La mención @handle se conservará en el texto, pero no se adjuntará ninguna referencia vacía."}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between text-xs text-[#a0a0a0]">
                <span>{t.characters?.savedViewsCount?.(views.length) || `${views.length} vistas disponibles`}</span>
                <span className="text-[11px] text-[#6b6b6b]">
                  {t.characters?.maxReferencesAllowed?.(maxAllowed) || `Máximo total de ${maxAllowed} referencias`}
                </span>
              </div>

              {/* Grid de vistas */}
              <div
                data-testid="character-views-grid"
                className="grid grid-cols-2 sm:grid-cols-3 gap-3"
              >
                {views.map((v) => {
                  const isSelected = selectedAssetIds.includes(v.assetId);
                  const asset = assetById[v.assetId];

                  return (
                    <div
                      key={v.id}
                      data-testid={`character-view-item-${v.assetId}`}
                      onClick={() => toggleViewSelection(v.assetId)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && toggleViewSelection(v.assetId)}
                      className={`group relative flex flex-col rounded-lg border overflow-hidden cursor-pointer transition-all ${
                        isSelected
                          ? "border-[#f5b544] bg-[#f5b544]/10 shadow-[0_0_12px_rgba(245,181,68,0.15)] ring-1 ring-[#f5b544]/50"
                          : "border-[#282828] bg-[#181818] hover:border-[#3d3d3d] hover:bg-[#1f1f1f]"
                      }`}
                    >
                      {/* Miniatura */}
                      <div className="relative aspect-square w-full bg-[#0d0d0d] overflow-hidden">
                        <MediaFrame
                          asset={asset}
                          fit="contain"
                          className="h-full w-full border-0 rounded-none bg-[#0d0d0d]"
                          emptyMessage={t.characters?.noImage || "Sin imagen"}
                        />

                        {/* Indicador de Selección */}
                        <div
                          className={`absolute top-2 right-2 h-5 w-5 rounded-full flex items-center justify-center transition-all ${
                            isSelected
                              ? "bg-[#f5b544] text-black shadow-md scale-100"
                              : "border border-white/40 bg-black/40 text-transparent scale-90 group-hover:border-white/70"
                          }`}
                        >
                          <Check size={12} strokeWidth={3} />
                        </div>

                        {/* Badge Principal */}
                        {v.isMaster && (
                          <div className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded text-[9px] font-mono font-medium bg-black/80 border border-amber-400/50 text-amber-300">
                            {t.characters?.master || "Principal"}
                          </div>
                        )}
                      </div>

                      {/* Etiqueta de la vista */}
                      <div className="p-2 flex flex-col gap-0.5 bg-[#141414] border-t border-[#222]">
                        <span className="text-xs font-medium text-[#ededed] truncate">
                          {v.label}
                        </span>
                        <span className="text-[10px] text-[#737373] capitalize">
                          {v.viewType || "vista"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Pie fijo */}
        <div className="flex items-center justify-between border-t border-[#242424] px-5 py-3.5 bg-[#181818]">
          <button
            type="button"
            data-testid="character-view-modal-cancel"
            onClick={onClose}
            className="v-btn-ghost v-btn-sm text-xs text-[#a0a0a0] hover:text-[#f4f1ec]"
          >
            {t.common?.cancel || "Cancelar"}
          </button>

          {hasViews && (
            <button
              type="button"
              data-testid="character-view-modal-confirm"
              onClick={handleConfirm}
              disabled={selectedAssetIds.length === 0}
              className="v-btn-primary v-btn-sm text-xs flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Check size={13} />
              <span>
                {selectedAssetIds.length > 0
                  ? t.characters?.linkViewsButtonCount?.(selectedAssetIds.length) ||
                    `Vincular ${selectedAssetIds.length} vista${selectedAssetIds.length > 1 ? "s" : ""}`
                  : t.characters?.linkViewsButton || "Seleccionar vistas"}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterViewSelectorModal;
